import * as litModule from "lit";
import { repeat } from "lit/directives/repeat.js";

import { FloatingUiController } from "./controllers/FloatingUiController";
import { ProductFormController } from "./controllers/ProductFormController";
import { WishlistStateController } from "./controllers/WishlistStateController";

import "./components/button";
import "./components/counter";
import "./components/icon";
import "./components/option-select";
import "./components/option-swatches";
import "./components/quantity-input";

import type { PropertyDeclarations, PropertyValues } from "lit";
import type { State, StateConfig } from "./controllers/WishlistStateController";
import type {
  ShopifyCart,
  ShopifyProduct,
  ShopifyProductVariant,
} from "./api/ShopifyApi";
import type { MediaSize } from "./utils/UrlUtil";
import type { ProductInfo } from "./state/WishlistState";
import type { Wishlist, WishlistItem } from "./api/WishlistApi";
import type { Subscription } from "./state/Observable";
import { LitComponent } from "./components/lit-component";
import type { MoneyFormatOptions } from "./utils/MoneyUtil";

export { FloatingUiController, ProductFormController, WishlistStateController };

// We need this export for backwards compat
export const lit = { ...litModule, repeat };

export class WishlistElement extends LitComponent {
  static properties: PropertyDeclarations = {
    wishlist: { state: true },
    productInfo: { state: true },
    wishlistItem: { state: true },
    cart: { state: true },
    loadWithoutSession: { type: Boolean, attribute: "load-without-session" },
  };

  wishlist?: Wishlist;
  productInfo?: ProductInfo;
  wishlistItem?: WishlistItem;
  cart?: ShopifyCart;

  subscription?: Subscription;
  observer?: IntersectionObserver;
  connected = false;
  stateCtrl: WishlistStateController;
  cleanupMethods: Array<() => void> = [];
  eventPending = false;
  firstConnect = true;
  loadWithoutSession = false;

  get app() {
    return this.stateCtrl.app;
  }

  static get observedAttributes() {
    return [
      ...super.observedAttributes,
      "data-wishlist-id",
      "data-wishlist-item-id",
      "data-product-handle",
      "data-product-id",
      "data-variant-id",
    ];
  }

  constructor() {
    super();

    this.stateCtrl = new WishlistStateController(this);
  }

  attributeChangedCallback(
    name: string,
    oldValue: string,
    newValue: string,
  ): void {
    super.attributeChangedCallback(name, oldValue, newValue);

    if (this.connected) {
      this.initState();
    }
  }

  appReadyCallback() {
    if (!this.dataset.productHandle) {
      return;
    }

    this.app.events.subscribe("product:change-variant:success", (event) => {
      if (
        this.dataset.productHandle === event.data?.productHandle &&
        event.data?.variantId
      ) {
        this.dataset.variantId = event.data.variantId.toString();
      }
    });
  }

  async connect() {
    if (this.connected) {
      return;
    }

    this.connected = true;
    this.init();
  }

  getStateConfig(): StateConfig & { params?: Record<string, string> } {
    return {};
  }

  getTranslation(id: string, values?: Record<string, string>) {
    if (!this.app.theme) {
      return id;
    }

    const text = this.app.theme.translate(id, values);
    if (id.endsWith("_html")) {
      return this.unsafeHTML(text);
    }

    return text;
  }

  formatMoney(value: string, options?: MoneyFormatOptions) {
    return this.app.utils.money.formatMoney(value, {
      withCurrency:
        options?.withCurrency ?? this.app.settings.money.withCurrency,
      withoutTrailingZeros: options?.withoutTrailingZeros,
    });
  }

  renderMoney(value: string, options?: MoneyFormatOptions) {
    return this.unsafeHTML(this.formatMoney(value, options));
  }

  getProductUrl(product: ShopifyProduct, variant?: ShopifyProductVariant) {
    return this.app.utils.url.getVariantUrl(product.url, variant?.id);
  }

  getImageUrl(
    product: ShopifyProduct,
    variant?: ShopifyProductVariant,
    size?: MediaSize,
  ) {
    return this.app.utils.url.getImageUrl(
      variant?.featured_image?.src ? variant : product,
      size,
    );
  }

  init() {
    const { loading } = this.getStateConfig();
    if (loading === "lazy") {
      this.initFallbackState();
      this.initObserver();
    } else {
      this.initState();
      this.appReadyCallback();
    }
  }

  hasState = false;

  setState(state: Partial<State>) {
    if (!this.hasState) {
      this.setAttribute("wk-loaded", "");
      this.hasState = true;
    }
    if (state.wishlist) {
      this.wishlist = state.wishlist;
    }
    if (state.wishlistItem) {
      this.wishlistItem = state.wishlistItem;
    }
    if (state.productInfo) {
      this.productInfo = state.productInfo;
    }
    if (state.cart) {
      this.cart = state.cart;
    }
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();

    // Workaround to prevent double ups in Vue
    if (this.innerHTML && this.firstConnect) {
      this.innerHTML = "";
    }

    this.initEvents();
    this.connect();
    this.firstConnect = false;
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.cleanupMethods.forEach((cleanup) => cleanup());
    this.connected = false;
    this.subscription?.unsubscribe();
    this.observer?.disconnect();
  }

  shouldUpdate(
    changedProperties: PropertyValues<any> | Map<PropertyKey, unknown>,
  ): boolean {
    if (!this.connected) {
      return false;
    }
    return super.shouldUpdate(changedProperties);
  }

  initState() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }

    this.subscription = this.stateCtrl.observe().subscribe((state) => {
      this.setState(state);
    });
  }

  initFallbackState() {
    const fallback = this.stateCtrl.getFallback();

    if (fallback) {
      this.setState(fallback);
    }
  }

  initObserver() {
    this.observer?.disconnect();

    this.observer = new IntersectionObserver(
      (entries) => this.handleIntersect(entries),
      {
        root: null,
        rootMargin: "0px",
      },
    );

    this.setAttribute("wk-lazy", "");

    this.observer.observe(this.firstElementChild ?? this);
  }

  handleIntersect([entry]: IntersectionObserverEntry[]) {
    if (entry.isIntersecting) {
      this.removeAttribute("wk-lazy");
      this.observer?.disconnect();
      this.initState();
      this.appReadyCallback();
    }
  }

  getEventConfig(): EventConfig {
    return {};
  }

  initEvents() {
    const eventConfig = this.getEventConfig();

    if (!eventConfig) {
      return;
    }

    const subscriptions = Object.entries(eventConfig).map(([key, handler]) => {
      const [eventName, selector] = key.split(/ (.*)/s);

      return this.subscribeToEvent({
        eventName,
        selector,
        handler,
      });
    });

    // TODO: Events need to be cleaned up?
    // this.cleanupMethods.push(() =>
    //   subscriptions.forEach((subscription) => subscription.unsubscribe())
    // );
  }

  subscribeToEvent({
    eventName,
    handler,
    selector,
  }: {
    eventName: string;
    selector?: string;
    handler: (event: Event, target?: EventTarget | null) => Promise<any> | any;
  }) {
    this.addEventListener(eventName, async (event) => {
      if (this.eventPending) {
        event.preventDefault();
        return;
      }

      this.eventPending = true;

      const findEventTarget = () => {
        if (!(event.target instanceof HTMLElement)) {
          return null;
        }
        return selector
          ? event.target.closest(selector)
          : (event.currentTarget as HTMLElement);
      };

      const target = findEventTarget();

      // if (!target) {
      //   this.eventPending = false;
      //   return;
      // }

      const executeEventHandler = async () => {
        return handler.bind(this)(event, target);
      };

      await executeEventHandler().catch((error) => {
        console.warn("Event handler failed:", error.message);
      });
      this.eventPending = false;
    });
  }
}

export type EventConfig = Record<string, EventHandler>;
export type EventHandler = (data: Event | Element) => void;

export class DialogElement extends LitComponent {
  static properties: PropertyDeclarations = {
    view: { state: true },
    active: { state: true },
  };

  view: string = "default";
  active = false;

  get app() {
    if (!window.WishlistKing) {
      throw new Error("Wishlist King SDK not initialized");
    }
    return window.WishlistKing;
  }
  protected updated(changedProperties: Map<PropertyKey, unknown>) {
    if (changedProperties.has("active")) {
      document.body.classList.toggle("wk-modal-dialog-open", this.active);
      if (this.active) {
        this.querySelector("dialog")?.showModal();
      } else {
        this.querySelector("dialog")?.close();
      }
    }
    if (changedProperties.has("view")) {
      this.setAutoFocus();
    }
  }

  handleClickEvent(event: MouseEvent) {
    if (event.target !== event.currentTarget) {
      return;
    }

    const bounds = (
      event.currentTarget as HTMLElement
    )?.getBoundingClientRect();

    const isInDialog =
      bounds.top <= event.clientY &&
      event.clientY <= bounds.top + bounds.height &&
      bounds.left <= event.clientX &&
      event.clientX <= bounds.left + bounds.width;

    if (!isInDialog) {
      this.close();
    }
  }

  handleCloseEvent() {
    this.active = false;
  }

  firstUpdated(): void {
    this.querySelector("dialog")?.addEventListener(
      "click",
      this.handleClickEvent.bind(this),
    );
    this.querySelector("dialog")?.addEventListener(
      "close",
      this.handleCloseEvent.bind(this),
    );
  }

  show() {
    if (!this.parentElement) {
      document.body.appendChild(this);
    }
    this.active = true;
  }

  close() {
    this.active = false;
  }

  setAutoFocus() {
    // Autofocus in dialog element doesn't work. This fixes it.
    setTimeout(() => {
      (this.querySelector("input[autofocus]") as HTMLElement)?.focus();
    }, 100);
  }

  createRenderRoot() {
    return this;
  }
}
